// Copyright 2020 Sorama B.V.

import { autoinject } from "aurelia-framework";
import {
    FileDTO,
    ImageDTO,
    LinkedMediaObjectDTO,
    NewFileDTO,
    NewImageDTO,
    NewTextBoxDTO,
    NewVideoDTO,
    TextBoxDTO,
    UpdateTextBoxDTO,
    VideoDTO,
} from "datatypes/SoramaWebApi";
import { Api } from "libraries/WebApi/Api";
import { IPagedRequest, IPageResult, IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { PagedRequest } from "libraries/WebRequestHandler/PagedRequest";
import { IDeletableRepository, IRetrievableRepository } from "./IGenericRepository";

export interface NewLinkedMediaObject<TMediaObject extends LinkedMediaObjectDTO> {
    /**
     * The actual linked media object
     */
    mediaObject: TMediaObject;

    /**
     * Indicates if the media object was newly created or an existing one was returned.
     */
    added: boolean;
}

export abstract class ILinkedMediaObjectRepository
    implements IRetrievableRepository<LinkedMediaObjectDTO>, IDeletableRepository
{
    /**
     * Get a LinkedMediaObject with the provided id.
     * @param id Id of the LinkedMediaObject to retrieve.
     */
    public abstract get(id: string): Promise<LinkedMediaObjectDTO>;

    /**
     * Get the data for a LinkedMediaObject.
     * @param id Id of the LinkedMediaObject to retrieve the data for.
     */
    public abstract getData(id: string): Promise<Blob>;

    /**
     * Gets all accessible images as a paged request.
     */
    public abstract getImages(queryFilter?: string): IPagedRequest<ImageDTO>;

    /**
     * Gets all accessible videos as a paged request.
     */
    public abstract getVideos(queryFilter?: string): IPagedRequest<VideoDTO>;

    /**
     * Gets all accessible files as a paged request.
     */
    public abstract getFiles(queryFilter?: string): IPagedRequest<FileDTO>;

    /**
     * Creates a new image LinkedMediaObject.
     * @param newImage Image data structure.
     */
    public abstract createImage(newImage: NewImageDTO): Promise<NewLinkedMediaObject<ImageDTO>>;

    /**
     * Creates a new video LinkedMediaObject.
     * @param newVideo Video data structure.
     */
    public abstract createVideo(newVideo: NewVideoDTO): Promise<NewLinkedMediaObject<VideoDTO>>;

    /**
     * Creates a new textbox LinkedMediaObject.
     * @param newTextbox Textbox data structure.
     */
    public abstract createTextbox(newTextbox: NewTextBoxDTO): Promise<TextBoxDTO>;

    /**
     * Creates a new file LinkedMediaObject.
     * @param newFile File data structure.
     */
    public abstract createFile(newFile: NewFileDTO): Promise<NewLinkedMediaObject<FileDTO>>;

    /**
     * Update the fields of a textbox media object.
     * @param id Id of the textbox media object.
     * @param update Update fields, not set fields will not be updated.
     */
    public abstract updateTextBox(id: string, update: UpdateTextBoxDTO): Promise<void>;

    /**
     * Delete a media object
     * @param id id of the media object to delete.
     */
    public abstract delete(id: string): Promise<void>;
}

@autoinject
export class LinkedMediaObjectRepository extends ILinkedMediaObjectRepository {
    constructor(private readonly webRequestHandler: IWebRequestHandler) {
        super();
    }

    public get(id: string): Promise<LinkedMediaObjectDTO> {
        return this.webRequestHandler
            .createGetRequest<LinkedMediaObjectDTO>(Api.media + "/" + id)
            .send()
            .then((x) => x.content);
    }

    public getData(id: string): Promise<Blob> {
        return this.webRequestHandler
            .createGetRequest(Api.media + "/" + id + "/Data")
            .withBinaryResponse()
            .withCache()
            .send()
            .then((x) => x.content);
    }

    public getImages(queryFilter?: string): IPagedRequest<ImageDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<ImageDTO>>(
            Api.mediaImages + (queryFilter ? "?" + queryFilter : "")
        );
        return PagedRequest.create(request);
    }

    public getVideos(queryFilter?: string): IPagedRequest<VideoDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<VideoDTO>>(
            Api.mediaVideos + (queryFilter ? "?" + queryFilter : "")
        );
        return PagedRequest.create(request);
    }

    public getFiles(queryFilter?: string): IPagedRequest<FileDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<FileDTO>>(
            Api.mediaFiles + (queryFilter ? "?" + queryFilter : "")
        );
        return PagedRequest.create(request);
    }

    public createImage(newImage: NewImageDTO): Promise<NewLinkedMediaObject<ImageDTO>> {
        return this.webRequestHandler
            .createPostRequest<ImageDTO>(Api.mediaImages, newImage)
            .send()
            .then((x) => {
                return {
                    mediaObject: x.content,
                    added: x.statusCode != 303, // see other
                };
            });
    }

    public createVideo(newVideo: NewVideoDTO): Promise<NewLinkedMediaObject<VideoDTO>> {
        return this.webRequestHandler
            .createPostRequest<VideoDTO>(Api.mediaVideos, newVideo)
            .send()
            .then((x) => {
                return {
                    mediaObject: x.content,
                    added: x.statusCode != 303, // see other
                };
            });
    }

    public createTextbox(newTextbox: NewTextBoxDTO): Promise<TextBoxDTO> {
        return this.webRequestHandler
            .createPostRequest<TextBoxDTO>(Api.mediaTextboxes, newTextbox)
            .send()
            .then((x) => {
                return x.content;
            });
    }

    public createFile(newFile: NewFileDTO): Promise<NewLinkedMediaObject<FileDTO>> {
        return this.webRequestHandler
            .createPostRequest<FileDTO>(Api.mediaFiles, newFile)
            .send()
            .then((x) => {
                return {
                    mediaObject: x.content,
                    added: x.statusCode != 303, // see other
                };
            });
    }

    public updateTextBox(id: string, update: UpdateTextBoxDTO): Promise<void> {
        return this.webRequestHandler
            .createPutRequest<void>(Api.mediaTextboxes + "/" + id, update)
            .send()
            .then(() => {});
    }

    public delete(id: string): Promise<void> {
        return this.webRequestHandler
            .createDeleteRequest(Api.media + "/" + id)
            .send()
            .then(() => {});
    }
}
